import {
  Component,
  afterNextRender,
  Injector,
  input,
  output,
  inject,
  signal,
  computed,
  ChangeDetectionStrategy,
  DestroyRef,
  effect,
  untracked,
  OnDestroy,
  ElementRef,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { UpperCasePipe } from '@angular/common';
import { Subscription, catchError, from, map, mergeMap, of, switchMap, toArray } from 'rxjs';
import { TranslocoService } from '@jsverse/transloco';
import { FormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import {
  MatAutocomplete,
  MatAutocompleteTrigger,
  MatAutocompleteModule,
} from '@angular/material/autocomplete';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatIconModule } from '@angular/material/icon';
import { TranslocoModule } from '@jsverse/transloco';
import { RefreshButtonComponent } from '../refresh-button/refresh-button';
import { AccessBannerComponent } from '../access-banner/access-banner';
import type { AssessmentLinkRow } from '../../models/httpApi.model';
import { ModalComponent, type ModalConfig } from '../modal/modal';
import { OrgLogoComponent } from '../org-logo/org-logo';
import { HuxleyComponent } from '../huxley/huxley';
import { ModalStatusComponent } from '../modal-status/modal-status';
import { StepIndicatorComponent, StepConfig } from '../step-indicator/step-indicator';
import { ContextDocumentUploadModal } from '../context-document-upload-modal/context-document-upload-modal';
import { TypeIconComponent, type TypeIconVariant } from '../type-icon/type-icon';
import { AiGeneratingStatusComponent } from '../ai-generating-status/ai-generating-status';
import { ButtonComponent } from '../button/button';
import { SkeletonDirective } from '../../shared/directives/skeleton.directive';
import { SelectComponent, type SelectOption } from '../select/select';
import { LanguagePickerComponent } from '../language-picker/language-picker';
import { LanguageBadgeComponent } from '../language-badge/language-badge';
import { ACTIVITY_STATE_LABELS } from '../../models';
import { StateStoreService } from '../../services/state-store/state-store';
import {
  LabelResponse,
  ContextDocument,
  ContextDocumentsResponse,
  EntityLanguage,
  AssessmentTypeEnum,
  MAX_CONTEXT_SOURCE_DOCUMENTS,
} from '../../models';
import {
  getContextDocExtractionDisplay,
  getVendorAssessmentLinkStatusDisplay,
  isContextDocTerminal,
  type ExtractionStatusDisplay,
} from '../../utils/status-indicator.utils';
import { StatusIndicatorComponent } from '../status-indicator/status-indicator';
import { QuestionnaireVersionService } from '../../services/questionnaire-version/questionnaire-version.service';
import type { Question } from '../questionnaire/questionnaire';
import type {
  CreateAssessmentErrorLink,
  CreateAssessmentModalConfirmEvent,
  CreateAssessmentPrefill,
} from '../../shared/create-assessment-handler';
import { OrgManagementApi } from '../../services/org-management-api/org-management-api';
import { VendorApi } from '../../services/vendor-api/vendor-api';
import type { ProcessingActivity } from '../../models/vendor/activity.model';
import type { VendorAssessmentLinkRow } from '../../models/vendor/assessment-link.model';
import {
  TRANSFER_MECHANISM_LABELS,
  TransferMechanism,
  type VendorUse,
} from '../../models/vendor/use.model';
import {
  VENDOR_PRIVACY_ROLE_LABELS,
  type VendorPrivacyRole,
} from '../../models/vendor/common.model';
import { ISO_3166_COUNTRIES, countryNameByCode } from '../../models/iso-3166.model';
import type { TiaGatePreviewResponse } from '../../models/assessments/tia.model';
import {
  ART49_DEROGATIONS,
  type Art49DerogationClaim,
  type Art49DerogationId,
  type TiaGateOutcome,
} from '../../models/assessments/tia.model';
import { getContextDocIconConfig, getContextDocFileTypeLabel } from '../../utils/document.utils';
import { getVendorLogoUrl } from '../../utils/vendor.utils';
import { buildAssessmentEditUrl } from '../../utils/navigation.utils';
import { isExemptOutcome } from '../assessments/tia/shared/tia-report.utils';
import {
  getPlugin,
  listPlugins,
  type AssessmentTypePlugin,
} from '../../services/assessments/assessment-type.registry';
import { deriveSelectionState } from '../../shared/picker-handler';
import { FieldSuggestionComponent } from '../field-suggestion/field-suggestion';
import { AssessmentDescriptionSuggestionService } from '../../services/assessments/assessment-description-suggestion.service';
import { getEntityLanguageLabel } from '../../utils/language.utils';
import { accessDenialMessage } from '../../utils/access-error.utils';
import { getUserFriendlyErrorMessage } from '../../utils/error.utils';

/**
 * Wizard step order. VER-284 added `type` at position 0 so users pick
 * DPIA vs AI Risk before naming the assessment. When the caller's org
 * only has ONE type entitled (single-plugin availability), the type
 * step is skipped and the modal opens at the first applicable authoring step.
 *
 * Description is authored before the final review; suggestions are temporary agent drafts.
 */
/** Context-doc picker row — raw doc plus every value the template would
 *  otherwise compute per change-detection pass. Extends `ContextDocument`
 *  so handlers taking the raw type still accept a row. */
interface ContextDocRow extends ContextDocument {
  selected: boolean;
  icon: string;
  iconVariant: TypeIconVariant;
  typeLabel: string;
  sizeLabel: string | null;
  extraction: ExtractionStatusDisplay | null;
}

interface OpenDpiaLink extends AssessmentLinkRow {
  name: string;
}
interface ExistingDpiaRow extends OpenDpiaLink {
  activityName: string;
  display: ReturnType<typeof getVendorAssessmentLinkStatusDisplay>;
  url: string;
}

type AssessmentModalStep =
  | 'type'
  | 'vendor'
  | 'link'
  | 'language'
  | 'details'
  | 'context-docs'
  | 'derogations'
  | 'processing-description'
  | 'review';

/** Every declared transfer mechanism has `transfer_mechanism.<VALUE>.{label,explanation}`
 *  in en.json / da.json (VER-381). */
const KNOWN_TRANSFER_MECHANISMS: ReadonlySet<string> = new Set(Object.values(TransferMechanism));

@Component({
  selector: 'app-create-assessment-modal',
  providers: [AssessmentDescriptionSuggestionService],
  imports: [
    FieldSuggestionComponent,
    FormsModule,
    UpperCasePipe,
    MatFormFieldModule,
    MatInputModule,
    MatAutocompleteModule,
    MatCheckboxModule,
    MatIconModule,
    TranslocoModule,
    ModalComponent,
    AccessBannerComponent,
    RefreshButtonComponent,
    OrgLogoComponent,
    HuxleyComponent,
    ModalStatusComponent,
    StepIndicatorComponent,
    ContextDocumentUploadModal,
    TypeIconComponent,
    AiGeneratingStatusComponent,
    ButtonComponent,
    SkeletonDirective,
    SelectComponent,
    LanguagePickerComponent,
    LanguageBadgeComponent,
    StatusIndicatorComponent,
  ],
  templateUrl: './create-assessment-modal.html',
  styleUrl: './create-assessment-modal.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CreateAssessmentModalComponent implements OnDestroy {
  private readonly stateService = inject(StateStoreService);
  private readonly transloco = inject(TranslocoService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly translationVersion = signal(0);
  private readonly orgApi = inject(OrgManagementApi);
  private readonly vendorApi = inject(VendorApi);
  private readonly router = inject(Router);

  /** VER-377 "Start a full TIA instead" — the record-only link the new
   *  TIA replaces (set via {@link prefill}); excluded from the duplicate
   *  pre-check since the BE supersedes it first. */
  private readonly _supersedesLinkId = signal<string | null>(null);
  private readonly questionnaireVersionService = inject(QuestionnaireVersionService);

  private readonly injector = inject(Injector);
  readonly labelAutocomplete = viewChild<MatAutocomplete>('auto');
  private readonly labelAutocompleteTrigger = viewChild(MatAutocompleteTrigger);
  readonly loadMoreLabelValue = '__load_more_labels__';
  readonly labelInputEl = viewChild<ElementRef<HTMLInputElement>>('labelInputEl');

  // ── Assessment-type selection (VER-284, shown as step 0 when > 1 type) ──
  //
  // `_selectedType` is nullable because a fresh open() clears it. The
  // "Next" button on the type step stays disabled until the user picks
  // a card. When only one type is available (feature-gated org),
  // `open()` pre-selects it and skips the type step entirely, so no
  // regression to the single-type flow.
  private _selectedType = signal<AssessmentTypeEnum | null>(null);
  readonly selectedType = this._selectedType.asReadonly();

  /** Every registered type startable from this generic modal. Stable —
   *  sourced from the plugin registry, not from the state store — so it
   *  doesn't race whoami / feature-flag loading. Drives step-visibility
   *  below.
   *
   *  `requiresVendor` types (TIA) are included as of VER-331 — the
   *  wizard's `vendor` step (see below) gives this modal somewhere to
   *  attach one, closing the gap the old comment here used to flag as a
   *  future ticket (P8). TIA also still starts from the vendor
   *  Transfers-tab CTA (pre-populated vendor context, no picker step
   *  needed there) — the two entry points are complementary, not
   *  either/or. */
  readonly registeredTypes = listPlugins();

  /** Per-card entitlement decision. Async-safe: a whoami still loading
   *  reports `false` (fail-closed on `hasFeature`), which shows the
   *  card in a disabled state rather than hiding it — so the picker
   *  is deterministic across page-load timing. */
  isTypeEntitled = (plugin: AssessmentTypePlugin): boolean =>
    this.stateService.hasFeature(plugin.featureId);

  /** Currently active plugin (selected type or AI-Risk default).
   *  Drives the modal title + label copy. Never auto-selects a
   *  registered type on the user's behalf. */
  readonly activePlugin = computed<AssessmentTypePlugin>(() => {
    const selected = this._selectedType();
    return selected ? getPlugin(selected) : getPlugin(AssessmentTypeEnum.AI_RISK_ASSESSMENT);
  });

  // ── PA-picker state (FE-G / VER-284) ────────────────────────────────
  //
  // The `link` step is shown only when the active plugin declares
  // `requiresPa === true` (DPIA today). Two radios: "linked" default
  // (needs a PA picked from the dropdown) vs "standalone" (no PA — the
  // user can materialise one later). Loaded lazily on entry to the step
  // so a caller that never picks DPIA never hits the vendor endpoint.
  private _isLinkedToPa = signal<boolean>(true);
  readonly isLinkedToPa = this._isLinkedToPa.asReadonly();
  private _selectedActivityId = signal<string | null>(null);
  readonly selectedActivityId = this._selectedActivityId.asReadonly();
  private _availableActivities = signal<ProcessingActivity[]>([]);
  readonly availableActivities = this._availableActivities.asReadonly();
  private _isLoadingActivities = signal<boolean>(false);
  readonly isLoadingActivities = this._isLoadingActivities.asReadonly();

  readonly existingDpias = signal<ReadonlyMap<string, OpenDpiaLink>>(new Map());
  readonly activityLoadFailed = signal(false);
  private activitiesSub: Subscription | null = null;
  readonly existingDpiaRows = computed<readonly ExistingDpiaRow[]>(() => {
    this.translationVersion();
    if (!this.paLinkRequired()) return [];
    const orgId = this.orgId() || this.stateService.selectedOrgId();
    return this._availableActivities().flatMap(activity => {
      const link = this.existingDpias().get(activity.activity_id);
      if (!link) return [];
      return [
        {
          ...link,
          activityName: activity.name,
          display: getVendorAssessmentLinkStatusDisplay(link.status),
          url: buildAssessmentEditUrl(this.router, link.assessment_id, orgId ?? undefined),
        },
      ];
    });
  });
  readonly activityCheck = signal<'idle' | 'loading' | 'ready' | 'error'>('idle');
  readonly openDpia = computed(
    () => this.existingDpias().get(this.selectedActivityId() ?? '') ?? null
  );
  private activityCheckSub: Subscription | null = null;
  readonly dpiaLinkBlocked = computed(
    () =>
      this.paLinkRequired() &&
      this.isLinkedToPa() &&
      (!this.selectedActivityId() || this.activityCheck() !== 'ready' || !!this.openDpia())
  );
  private readOpenDpia(orgId: string, activityId: string) {
    return this.orgApi.listAssessmentsForPa(orgId, activityId).pipe(
      switchMap(({ links }) => {
        const link = links.find(
          l =>
            l.assessment_type === AssessmentTypeEnum.DPIA &&
            (l.status === 'DRAFT' || l.status === 'APPROVED')
        );
        if (!link) return of(null);
        const fallback = this.transloco.translate<string>('assessment_types.dpia_label');
        return this.orgApi.getAssessment(orgId, link.assessment_id).pipe(
          map(({ assessment }) => ({ ...link, name: assessment.assessment_name || fallback })),
          catchError(() => of({ ...link, name: fallback }))
        );
      })
    );
  }

  /** Re-read authoritative ownership on selection and immediately before create. */
  checkActivity(onEligible?: () => void): void {
    this.activityCheckSub?.unsubscribe();
    const orgId = this.orgId() || this.stateService.selectedOrgId();
    const activityId = this.selectedActivityId();
    if (!this.paLinkRequired() || !this.isLinkedToPa() || !orgId || !activityId) {
      this.activityCheck.set('idle');
      return;
    }
    this.activityCheck.set('loading');
    this.activityCheckSub = this.readOpenDpia(orgId, activityId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: link => {
          this.existingDpias.update(links => {
            const next = new Map(links);
            if (link) next.set(activityId, link);
            else next.delete(activityId);
            return next;
          });
          this.activityCheck.set('ready');
          // Moving back while the check is running must never create an assessment.
          if (link && this.errorMessage() && this.currentStep() === 'review') this.setStep('link');
          if (onEligible && this.currentStep() === 'review') {
            if (link) this.setStep('link');
            else onEligible();
          }
        },
        error: () => this.activityCheck.set('error'),
      });
  }

  /** Keep unavailable activities visible with an explicit reason. They
   *  cannot be selected; their existing assessments are linked above. */
  readonly activitySelectOptions = computed<readonly SelectOption[]>(() => {
    this.translationVersion();
    const openTia = this.activityIdsWithOpenTia();
    const openTiaSuffix = this.transloco.translate('tia.wizard.activity_has_open_tia');
    const openDpiaSuffix = this.transloco.translate(
      'modals.create_assessment.link_activity_unavailable'
    );
    return this._availableActivities().map(activity => {
      const disabled = this.paLinkRequired() && this.existingDpias().has(activity.activity_id);
      return {
        value: activity.activity_id,
        label: activity.name,
        description:
          ACTIVITY_STATE_LABELS[activity.state] +
          (disabled
            ? ` · ${openDpiaSuffix}`
            : openTia.has(activity.activity_id)
              ? ` · ${openTiaSuffix}`
              : ''),
        disabled,
        icon: disabled ? 'lock' : undefined,
      };
    });
  });

  /** Name of the linked PA, for the review step's "Processing activity"
   *  row. `null` when standalone (or link never shown) — the template
   *  falls back to "Not linked" in that case. */
  readonly linkedActivityName = computed<string | null>(() => {
    if (!this._isLinkedToPa()) return null;
    const id = this._selectedActivityId();
    if (!id) return null;
    return this._availableActivities().find(a => a.activity_id === id)?.name ?? null;
  });

  /** True when the currently-selected type is DPIA-shaped (mandatory
   *  link) OR vendor-shaped (TIA — optional link, VER-331 step 3).
   *  `paLinkRequired` distinguishes the two for the toggle's default
   *  position; the step itself is shared unchanged between both. */
  readonly showLinkStep = computed(
    () => this.activePlugin().requiresPa || this.activePlugin().requiresVendor
  );
  readonly paLinkRequired = computed(() => this.activePlugin().requiresPa);

  /** Gates both the `vendor` and `derogations` steps — both are
   *  vendor-shaped-type-only (TIA today). */
  readonly showVendorStep = computed(() => this.activePlugin().requiresVendor);

  // ── Vendor picker (VER-331 step 2) ──────────────────────────────────
  //
  // Shown only for `requiresVendor` types (TIA today). Selecting a vendor
  // immediately runs the read-only gate-preview check; the wizard can
  // only proceed past this step when the outcome is `proceed`. Nothing
  // is created here — cancelling the wizard at any point leaves no
  // orphan record, per the ticket's explicit acceptance criterion.
  private _vendorList = signal<VendorUse[]>([]);
  readonly vendorList = this._vendorList.asReadonly();
  private _isLoadingVendors = signal<boolean>(false);
  readonly isLoadingVendors = this._isLoadingVendors.asReadonly();
  private _selectedVendorId = signal<string | null>(null);
  readonly selectedVendorId = this._selectedVendorId.asReadonly();
  private _gatePreview = signal<TiaGatePreviewResponse | null>(null);
  readonly gatePreview = this._gatePreview.asReadonly();
  private _isLoadingGatePreview = signal<boolean>(false);
  readonly isLoadingGatePreview = this._isLoadingGatePreview.asReadonly();
  private _gatePreviewError = signal<string | null>(null);
  readonly gatePreviewError = this._gatePreviewError.asReadonly();
  /** True when the gate-preview 400'd specifically because the vendor has
   *  no resolvable destination country (no storage/processing locations,
   *  no registry HQ country) — a common state for a newly-added or
   *  unenriched vendor, not a real failure. Offers a manual country
   *  picker instead of leaving the wizard on a dead-end error with no
   *  way forward (previously the only recourse was picking a different
   *  vendor). */
  private _gatePreviewNeedsManualCountry = signal<boolean>(false);
  readonly gatePreviewNeedsManualCountry = this._gatePreviewNeedsManualCountry.asReadonly();
  protected readonly manualCountryCode = signal<string | null>(null);
  protected readonly manualCountryOptions = computed<readonly SelectOption[]>(() =>
    ISO_3166_COUNTRIES.map(c => ({ value: c.code, label: c.name }))
  );

  protected readonly logoUrlForVendor = (v: VendorUse): string | null =>
    getVendorLogoUrl(v.global_vendor?.website ?? null);

  // Display labels for the vendor step's pre-populated rows — the gate
  // preview returns raw enum values (`ADEQUACY_DECISION`, `PROCESSOR`, `US`).
  protected readonly countryName = countryNameByCode;
  /** VER-381 — translated (EN/DA) label for a declared transfer
   *  mechanism. An unknown future value falls back to the English map,
   *  then to a de-snake-cased string — never the raw enum. */
  protected readonly transferMechanismLabel = (mechanism: string): string => {
    this.translationVersion();
    if (KNOWN_TRANSFER_MECHANISMS.has(mechanism)) {
      return this.transloco.translate(`transfer_mechanism.${mechanism}.label`);
    }
    return (
      TRANSFER_MECHANISM_LABELS[mechanism as TransferMechanism] ??
      mechanism.toLowerCase().replace(/_/g, ' ')
    );
  };

  /** Plain-language "why it is / isn't needed" line under the label. */
  protected readonly transferMechanismExplanation = (mechanism: string): string | null => {
    this.translationVersion();
    return KNOWN_TRANSFER_MECHANISMS.has(mechanism)
      ? this.transloco.translate(`transfer_mechanism.${mechanism}.explanation`)
      : null;
  };
  protected readonly privacyRoleLabel = (role: string): string =>
    VENDOR_PRIVACY_ROLE_LABELS[role as VendorPrivacyRole] ?? role;

  readonly selectedVendor = computed<VendorUse | null>(
    () => this._vendorList().find(v => v.vendor_id === this._selectedVendorId()) ?? null
  );

  /** "Assess anyway" (VER-377) — the user chose a voluntary full TIA for
   *  a destination with an in-force adequacy decision. Skips the
   *  Derogations step (no Art. 49 claim is needed) and submits with
   *  `full_assessment: true`. Cleared whenever the gate preview re-runs. */
  private _assessAnyway = signal<boolean>(false);
  readonly assessAnyway = this._assessAnyway.asReadonly();

  /** "Assess anyway" is offered for an adequacy decision only (never EEA)
   *  and only once the country has a published risk assessment — the
   *  same reason `pending` is a dead end for a required TIA. */
  readonly canAssessAnyway = computed(() => {
    const preview = this._gatePreview();
    return (
      preview?.outcome === 'adequate' &&
      !!preview.assess_anyway_allowed &&
      !!preview.country_published
    );
  });

  /** Next is enabled once a vendor's gate-preview outcome is `proceed`,
   *  or `adequate` with "Assess anyway" chosen — otherwise `adequate` and
   *  `pending` are dead ends for that vendor, not a loading state. */
  readonly canProceedFromVendorStep = computed(() => {
    if (!this._selectedVendorId()) return false;
    const outcome = this._gatePreview()?.outcome;
    return outcome === 'proceed' || (outcome === 'adequate' && this._assessAnyway());
  });

  toggleAssessAnyway(): void {
    if (!this.canAssessAnyway()) return;
    this._assessAnyway.update(v => !v);
  }

  private loadVendorsIfNeeded(): void {
    if (this._vendorList().length > 0 || this._isLoadingVendors()) return;
    const orgId = this.orgId() || this.stateService.selectedOrgId();
    if (!orgId) return;
    this._isLoadingVendors.set(true);
    this.vendorApi
      .listVendorUses(orgId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: uses => {
          this._vendorList.set(uses);
          this._isLoadingVendors.set(false);
        },
        error: err => {
          console.error('Failed to load vendors:', err);
          this._isLoadingVendors.set(false);
        },
      });
  }

  /** Picking a vendor immediately runs Gate 1 — no explicit country
   *  step; `country_code` is omitted so the BE resolves the vendor's
   *  known destination via its storage → processing → HQ waterfall. */
  selectVendor(vendorId: string): void {
    this._selectedVendorId.set(vendorId);
    this.loadVendorTiaLinks(vendorId);
    this.manualCountryCode.set(null);
    this._gatePreviewNeedsManualCountry.set(false);
    this.runGatePreview(vendorId, undefined);
  }

  /** Manual country override — offered when `runGatePreview` 400s with
   *  `no_destination_country` (see its doc comment). Re-runs the same
   *  read-only check with the user-supplied country instead of leaving
   *  the wizard on a dead end. */
  retryGatePreviewWithManualCountry(): void {
    const vendorId = this._selectedVendorId();
    const countryCode = this.manualCountryCode();
    if (!vendorId || !countryCode) return;
    this.runGatePreview(vendorId, countryCode);
  }

  private runGatePreview(vendorId: string, countryCode: string | undefined): void {
    this._gatePreview.set(null);
    this._gatePreviewError.set(null);
    this._assessAnyway.set(false);
    const orgId = this.orgId() || this.stateService.selectedOrgId();
    if (!orgId) return;
    this._isLoadingGatePreview.set(true);
    this.vendorApi
      .getVendorAssessmentGatePreview(orgId, vendorId, countryCode)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: preview => {
          this._gatePreview.set(preview);
          this._gatePreviewNeedsManualCountry.set(false);
          this._isLoadingGatePreview.set(false);
        },
        error: (err: { status?: number; error?: { code?: string } }) => {
          console.error('Failed to run TIA gate preview:', err);
          const needsManualCountry =
            err?.status === 400 && err?.error?.code === 'no_destination_country';
          this._gatePreviewNeedsManualCountry.set(needsManualCountry);
          this._gatePreviewError.set(
            this.transloco.translate(
              needsManualCountry
                ? 'tia.wizard.vendor_gate_preview_no_destination'
                : 'tia.wizard.vendor_gate_preview_error'
            )
          );
          this._isLoadingGatePreview.set(false);
        },
      });
  }

  // ── Duplicate pre-check (VER-380) ───────────────────────────────────
  //
  // The BE allows one open (DRAFT|APPROVED) TIA per (vendor, country,
  // activity). Load the vendor's TIA links once a vendor is picked so the
  // Link step can flag a clash before submit instead of failing with a
  // 409 at the very end. Best-effort: a failed load just skips the
  // warning — the BE's readable 409 still catches it.
  private _vendorTiaLinks = signal<VendorAssessmentLinkRow[]>([]);

  private loadVendorTiaLinks(vendorId: string): void {
    this._vendorTiaLinks.set([]);
    const orgId = this.orgId() || this.stateService.selectedOrgId();
    if (!orgId) return;
    this.vendorApi
      .listVendorAssessments(orgId, vendorId, 'TIA')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: res => {
          if (this._selectedVendorId() === vendorId) this._vendorTiaLinks.set(res.links ?? []);
        },
        error: err => console.error('Failed to load existing TIAs for vendor:', err),
      });
  }

  /** Open TIA links for the resolved destination country. */
  private readonly openTiaLinksForCountry = computed(() => {
    const countryCode = this._gatePreview()?.country_code;
    if (!countryCode) return [];
    return this._vendorTiaLinks().filter(
      l => (l.status === 'DRAFT' || l.status === 'APPROVED') && l.country_code === countryCode
    );
  });

  readonly activityIdsWithOpenTia = computed(
    () =>
      new Set(
        this.openTiaLinksForCountry()
          .map(l => l.activity_id)
          .filter(Boolean) as string[]
      )
  );

  /** The open link the current vendor + country + activity selection
   *  would collide with — `null` when the start would succeed. Matches
   *  the BE's `COALESCE(activity_id, '')` scoping: a standalone TIA
   *  clashes only with another standalone one. */
  readonly duplicateOpenTia = computed<VendorAssessmentLinkRow | null>(() => {
    if (!this.showVendorStep()) return null;
    const activityId = this._isLinkedToPa() ? this._selectedActivityId() : null;
    if (this._isLinkedToPa() && !activityId) return null;
    return (
      this.openTiaLinksForCountry().find(
        l => (l.activity_id ?? null) === activityId && l.id !== this._supersedesLinkId()
      ) ?? null
    );
  });

  readonly duplicateOpenTiaIsRecordOnly = computed(() =>
    isExemptOutcome(this.duplicateOpenTia()?.gate_outcome as TiaGateOutcome | null | undefined)
  );

  readonly duplicateOpenTiaUrl = computed(() => {
    const dup = this.duplicateOpenTia();
    const orgId = this.orgId() || this.stateService.selectedOrgId();
    return dup ? buildAssessmentEditUrl(this.router, dup.assessment_id, orgId ?? undefined) : null;
  });

  /** Apply a {@link prefill}: TIA type, the vendor's gate preview for the
   *  known country, and the activity link — then start on the vendor
   *  step so the user sees the gate outcome before continuing. */
  private applyPrefill(prefill: CreateAssessmentPrefill): void {
    this._selectedType.set(AssessmentTypeEnum.TIA);
    this._supersedesLinkId.set(prefill.supersedesLinkId);
    this.loadVendorsIfNeeded();
    this._selectedVendorId.set(prefill.vendorId);
    this.loadVendorTiaLinks(prefill.vendorId);
    this.runGatePreview(prefill.vendorId, prefill.countryCode);
    this._isLinkedToPa.set(!!prefill.activityId);
    this._selectedActivityId.set(prefill.activityId);
    this.loadActivitiesIfNeeded();
    // The rest of the stored screening data (VER-383 "Create TIA").
    if (prefill.assessmentName) this.assessmentName.set(prefill.assessmentName);
    if (prefill.language) {
      this.assessmentLanguage.set(prefill.language);
      this._languageManuallySet.set(true);
    }
    if (prefill.labels) {
      this.selectedLabels.set(
        Object.entries(prefill.labels).map(([label_id, label_name]) => ({ label_id, label_name }))
      );
    }
    if (prefill.contextDocumentIds?.length) {
      const ids = [...new Set(prefill.contextDocumentIds)];
      this.selectedContextDocIds.set(new Set(ids));
      this.contextDocSelectionLimitReached.set(ids.length > MAX_CONTEXT_SOURCE_DOCUMENTS);
    }
    const known = new Set<string>(ART49_DEROGATIONS.map(o => o.id));
    const ids = (prefill.derogation?.derogation_ids ?? []).filter(id => known.has(id));
    if (ids.length) this._selectedDerogationIds.set(new Set(ids));
    if (prefill.derogation?.rationale) this._derogationRationale.set(prefill.derogation.rationale);
    this.setStep('vendor');
  }

  /** "Change vendor" — collapses the summary card back to the picker. */
  changeVendor(): void {
    this._selectedVendorId.set(null);
    this._vendorTiaLinks.set([]);
    this._assessAnyway.set(false);
    this._gatePreview.set(null);
    this._gatePreviewError.set(null);
    this._gatePreviewNeedsManualCountry.set(false);
    this.manualCountryCode.set(null);
  }

  // ── Derogations (VER-331 step 6, multi-select since VER-377/VER-382) ─
  //
  // Tick one or more Art. 49 derogations, or "None of these apply" —
  // mutually exclusive: ticking "None" clears the derogations, ticking a
  // derogation clears "None". Any ticked derogation needs one shared
  // rationale; the footer then offers the outcome (VER-383):
  //   Record derogation — no TIA needed; the BE records it (exempt)
  //   Continue with TIA — a full, voluntary TIA (→ Review → Create)
  // Skipped entirely when "Assess anyway" was chosen on the vendor step.
  readonly derogationOptions = ART49_DEROGATIONS;
  private _selectedDerogationIds = signal<ReadonlySet<Art49DerogationId>>(new Set());
  readonly selectedDerogationIds = this._selectedDerogationIds.asReadonly();
  private _noneApply = signal<boolean>(false);
  readonly noneApply = this._noneApply.asReadonly();
  private _derogationRationale = signal<string>('');
  readonly derogationRationale = this._derogationRationale.asReadonly();
  private _derogationOutcome = signal<'record' | 'full' | null>(null);
  readonly showDerogationsStep = computed(() => this.showVendorStep() && !this._assessAnyway());

  /** Ticked derogations in picker order (Set iteration order would follow
   *  click order, which reads oddly on the Review step). */
  readonly selectedDerogationList = computed<Art49DerogationId[]>(() =>
    ART49_DEROGATIONS.map(o => o.id).filter(id => this._selectedDerogationIds().has(id))
  );

  readonly hasDerogationClaim = computed(() => this._selectedDerogationIds().size > 0);

  /** "Vendor — Activity (Country)" — names the transfer in user-facing
   *  copy (the record-only success message). */
  readonly transferSubject = computed<string | null>(() => {
    const vendor = this.selectedVendor()?.global_vendor?.name;
    if (!vendor) return null;
    const country = this.countryName(this._gatePreview()?.country_code);
    const activity = this.linkedActivityName();
    const head = activity ? `${vendor} — ${activity}` : vendor;
    return country ? `${head} (${country})` : head;
  });

  /** True when the submission will only record the derogation — no TIA
   *  questionnaire follows, so the Create CTA reads "Record derogation". */
  readonly isRecordOnly = computed(
    () =>
      this.canRecordOnly() &&
      this.showDerogationsStep() &&
      this.hasDerogationClaim() &&
      this._derogationOutcome() === 'record'
  );

  /** Replacing a recorded derogation ("Start a full TIA instead") is by
   *  definition a full TIA — the "record only" outcome isn't offered. */
  readonly canRecordOnly = computed(() => !this._supersedesLinkId());

  readonly canProceedFromDerogationsStep = computed(() => {
    if (this._noneApply()) return true;
    if (!this.hasDerogationClaim()) return false;
    return this._derogationRationale().trim().length > 0;
  });

  toggleDerogation(id: Art49DerogationId): void {
    // VER-382 — picking a derogation clears "None of these apply".
    this._noneApply.set(false);
    this._selectedDerogationIds.update(current => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  toggleNoneApply(): void {
    const next = !this._noneApply();
    this._noneApply.set(next);
    if (next) {
      this._selectedDerogationIds.set(new Set());
      this._derogationRationale.set('');
      this._derogationOutcome.set(null);
    }
  }

  updateDerogationRationale(value: string): void {
    this._derogationRationale.set(value);
  }

  private resetDerogations(): void {
    this._selectedDerogationIds.set(new Set());
    this._noneApply.set(false);
    this._derogationRationale.set('');
    this._derogationOutcome.set(null);
  }

  // ── Pre-questionnaire step (phase 0 / two-pass suggestions) ─────────
  //
  // Authored before creation. The question row (wording,
  // help text, Danish translation) is read from the questionnaire API —
  // never hard-coded here — so the wizard and the questionnaire stay
  // single-sourced.

  /** True when the active type's questionnaire has a phase 0. */
  readonly showPreQuestionnaireStep = computed(() => this.activePlugin().hasPreQuestionnairePhase);

  private questionnaireDefinitionSub: Subscription | null = null;
  private readonly _descriptionReviewed = signal(false);
  private readonly _questionnaireVersion = signal<string | null>(null);
  readonly descriptionSuggestion = inject(AssessmentDescriptionSuggestionService);
  private readonly _descriptionEdits = signal<Record<string, { previous: string; value: string }>>(
    {}
  );
  private readonly _dismissedDescriptions = signal<ReadonlySet<string>>(new Set());
  private readonly _isLoadingQuestionnaire = signal(false);
  /** True while the questionnaire definition is in flight — distinguishes
   *  "still fetching the question" from "the fetch failed", so the step
   *  doesn't flash its dead-end fallback during a slow load. */
  readonly isLoadingPreQuestionnaire = this._isLoadingQuestionnaire.asReadonly();

  /**
   * Phase-0 questions for the active type, localised to the assessment's
   * chosen language (not the app locale — the assessment is authored in
   * one language and the AI answers in it).
   *
   * The fetched definition's version travels with the final create request, so a newly
   * published version cannot change the questions after the user reviewed them.
   */
  readonly preQuestionnaireQuestions = computed<readonly Question[]>(() => {
    if (!this.showPreQuestionnaireStep()) return [];
    const slug = this.activePlugin().questionnaireSlug;
    const structure = this.questionnaireVersionService.getStructure(
      slug,
      undefined,
      this.assessmentLanguage().toLowerCase()
    );
    if (!structure) return [];
    return this.questionnaireVersionService.getPreQuestionnaireQuestions(structure);
  });

  /** Answers typed / accepted in the pre-questionnaire step, keyed by
   *  question key. Persisted with the final create request. */
  private readonly _preQuestionnaireAnswers = signal<Record<string, string>>({});
  /**
   * Per-question view rows for the step: the question copy, the help text
   * variant that matches whether a suggestion is coming, the suggestion
   * itself, and the current answer. Baked into one computed so the
   * template reads fields instead of calling four helpers per row.
   *
   * Every row renders as a textarea regardless of the question's declared
   * type — a pre-questionnaire question is a long-form description by
   * design (the wizard is not a second questionnaire renderer). If a
   * future phase 0 ever needs options, this step needs the real question
   * renderer, not a widened switch here.
   */
  readonly preQuestionnaireRows = computed(() =>
    this.preQuestionnaireQuestions().map(question => {
      const suggestion = this.descriptionSuggestion.result()?.recommendations[question.id] ?? null;
      const suggestedValue =
        suggestion?.value?.trim() && suggestion.value.length <= 4000 ? suggestion.value : null;
      // §2b: the default help text promises "the agent will pre-populate
      // this from the linked processing activity". When no suggestion is
      // coming that promise is false, so the unassisted variant wins —
      // falling back to the default whenever the API serves no variant.
      const assisted = this.descriptionSuggestion.state() === 'loading' || suggestedValue !== null;
      const helpText = assisted
        ? question.helpText
        : (question.helpTextUnassisted ?? question.helpText);
      return {
        id: question.id,
        required: question.required !== false,
        question: question.question,
        helpText,
        suggestedValue,
        reasoning: suggestion?.reasoning ?? null,
        // Keep uncertainty visible before the user chooses whether to use the answer.
        isTentative: suggestion?.confidence === 'medium',
        answer: this._preQuestionnaireAnswers()[question.id] ?? '',
        applied:
          !!suggestedValue && this._preQuestionnaireAnswers()[question.id] === suggestedValue,
        quiet:
          !!this._preQuestionnaireAnswers()[question.id]?.trim() &&
          this._preQuestionnaireAnswers()[question.id] !== suggestedValue,
        canUndo: this._descriptionEdits()[question.id]?.value === suggestedValue,
        dismissed: this._dismissedDescriptions().has(question.id),
      };
    })
  );

  /** Pass 1 is still running — show the waiting row, no suggestion box. */
  readonly isWaitingForSuggestion = computed(
    () => this.descriptionSuggestion.state() === 'loading'
  );

  /** Pass 1 reported a failure (or never settled). The user can still
   *  write the answer themselves; this only explains the empty box. */
  readonly suggestionFailed = computed(() => this.descriptionSuggestion.state() === 'failed');

  /** Required descriptions must be valid before Review and final creation. */
  readonly canConfirmPreQuestionnaire = computed(() => {
    const answers = this._preQuestionnaireAnswers();
    return (
      !this.isLoadingPreQuestionnaire() &&
      this.preQuestionnaireQuestions().length > 0 &&
      this.preQuestionnaireQuestions().every(
        q =>
          (answers[q.id] ?? '').length <= 4000 &&
          (q.required === false || (answers[q.id] ?? '').trim().length > 0)
      )
    );
  });

  onPreQuestionnaireAnswerChange(questionId: string, value: string): void {
    this._descriptionReviewed.set(false);
    this._preQuestionnaireAnswers.update(answers => ({ ...answers, [questionId]: value }));
  }

  /** "Use this answer" — copies the suggestion into the textarea. The
   *  user can then edit it; nothing is saved until they confirm. */
  applyPreQuestionnaireSuggestion(questionId: string): void {
    const row = this.preQuestionnaireRows().find(r => r.id === questionId);
    if (!row?.suggestedValue) return;
    this._descriptionEdits.update(edits => ({
      ...edits,
      [questionId]: { previous: row.answer, value: row.suggestedValue! },
    }));
    this.onPreQuestionnaireAnswerChange(questionId, row.suggestedValue);
  }

  undoPreQuestionnaireSuggestion(questionId: string): void {
    const edit = this._descriptionEdits()[questionId];
    if (!edit || this._preQuestionnaireAnswers()[questionId] !== edit.value) return;
    this.onPreQuestionnaireAnswerChange(questionId, edit.previous);
    this._descriptionEdits.update(edits => {
      const next = { ...edits };
      delete next[questionId];
      return next;
    });
  }

  dismissPreQuestionnaireSuggestion(questionId: string): void {
    this._dismissedDescriptions.update(ids => new Set([...ids, questionId]));
  }

  retryDescriptionSuggestion(): void {
    this._dismissedDescriptions.set(new Set());
    this._descriptionEdits.set({});
    this.descriptionSuggestion.load(true);
  }

  /** Fetch the questionnaire definition for the active type, so the
   *  step's copy is in hand by the time the user reaches it. Cached per
   *  (slug, version) by the service — repeat calls are free. No refetch
   *  on a language change: the response carries both the default and the
   *  `_da` strings, and `getStructure` re-maps from the cache. */
  loadPreQuestionnaireDefinition(): void {
    if (!this.showPreQuestionnaireStep() || this._isLoadingQuestionnaire()) return;
    const slug = this.activePlugin().questionnaireSlug;
    this._isLoadingQuestionnaire.set(true);
    this.questionnaireDefinitionSub = this.questionnaireVersionService
      .loadQuestionnaire(slug)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: definition => {
          this._questionnaireVersion.set(definition.version);
          this._isLoadingQuestionnaire.set(false);
        },
        error: err => {
          console.error('Failed to load questionnaire definition:', err);
          this._isLoadingQuestionnaire.set(false);
        },
      });
  }

  // Step wizard state — initial value is set by `open()` based on how
  // many types are available (see `open()` in the constructor effect).
  private _currentStep = signal<AssessmentModalStep>('details');
  readonly currentStep = this._currentStep.asReadonly();

  /**
   * High-water mark for the step timeline. Advances only on forward
   * navigation (`onConfirmed`); Back and timeline-click leave it in
   * place. Reset by `open()` and `close()` to whatever the fresh
   * initial step is, so a re-open starts clean.
   *
   * Uses the same `stepConfigs()` list for indexing since it can be
   * shorter than the AssessmentModalStep enum (multi-type / DPIA
   * conditionally include 'type' / 'link').
   */
  private readonly _maxReachedStep = signal<AssessmentModalStep>('details');
  readonly maxReachedStep = this._maxReachedStep.asReadonly();

  /** Forward-move helper — pairs step-set with high-water bookkeeping. */
  private setStep(next: AssessmentModalStep): void {
    this._currentStep.set(next);
    const list = this.stepConfigs();
    const nextIdx = list.findIndex(s => s.key === next);
    const maxIdx = list.findIndex(s => s.key === this._maxReachedStep());
    if (nextIdx > maxIdx) {
      this._maxReachedStep.set(next);
    }
  }

  /**
   * Timeline click — jump to a previously-visited step. All form state
   * (selected type, PA link, assessment name, labels, context docs)
   * persists across jumps, so revisiting is lossless. The step-
   * indicator already gates to visited-and-not-current; verify again.
   */
  protected onStepClicked(key: string): void {
    if (this.isTerminalState() || this.creationNeedsRecovery()) return;
    const list = this.stepConfigs();
    const clickedIdx = list.findIndex(s => s.key === key);
    const maxIdx = list.findIndex(s => s.key === this._maxReachedStep());
    if (clickedIdx < 0 || clickedIdx > maxIdx) return;
    const linkIdx = list.findIndex(step => step.key === 'link');
    if (clickedIdx > linkIdx && linkIdx >= 0 && (this.dpiaLinkBlocked() || this.duplicateOpenTia()))
      return;
    if (key === 'review' && this.showPreQuestionnaireStep() && !this._descriptionReviewed()) {
      this.setStep('processing-description');
      return;
    }
    this._currentStep.set(key as AssessmentModalStep);
  }
  readonly stepConfigs = computed<StepConfig[]>(() => {
    this.translationVersion();
    const configs: StepConfig[] = [];
    // Only surface the type step when the user actually has a choice —
    // otherwise the indicator would show a phantom step they never see.
    if (this.registeredTypes.length > 1) {
      configs.push({
        key: 'type',
        label: this.transloco.translate('modals.create_assessment.step_type'),
        detail: this._selectedType()
          ? this.transloco.translate(this.activePlugin().labelKey)
          : undefined,
      });
    }
    // VER-331 step 2 — vendor-shaped types only (TIA today).
    if (this.showVendorStep()) {
      configs.push({
        key: 'vendor',
        label: this.transloco.translate('tia.wizard.step_vendor'),
        detail: this.selectedVendor()?.global_vendor?.name,
      });
    }
    // FE-G: PA-picker step — mandatory for DPIA-shaped types
    // (plugin.requiresPa), optional for vendor-shaped types (TIA).
    if (this.showLinkStep()) {
      configs.push({
        key: 'link',
        label: this.transloco.translate('modals.create_assessment.step_link'),
        detail: this.linkedActivityName() ?? undefined,
      });
    }
    // Language step sits between classification (type/link) and content
    // authoring (details). Placed BEFORE details because the assessment
    // name auto-fill on DPIA use the PA name in the assessment's
    // language, and details already renders language-scoped context.
    configs.push(
      {
        key: 'language',
        label: this.transloco.translate('language_step.step_label'),
        detail: getEntityLanguageLabel(this.assessmentLanguage()),
      },
      { key: 'details', label: this.transloco.translate('modals.create_assessment.step_details') },
      {
        key: 'context-docs',
        label: this.transloco.translate('modals.create_assessment.step_context_docs'),
        detail: this.selectedContextDocIds().size
          ? this.transloco.translate('step_indicator.selected_count', {
              count: this.selectedContextDocIds().size,
            })
          : undefined,
      }
    );
    // VER-331 step 6 — vendor-shaped types only (TIA today), and not
    // after "Assess anyway" (no Art. 49 claim is needed).
    if (this.showDerogationsStep()) {
      configs.push({
        key: 'derogations',
        label: this.transloco.translate('tia.wizard.step_derogations'),
      });
    }
    if (this.showPreQuestionnaireStep()) {
      configs.push({
        key: 'processing-description',
        label: this.transloco.translate('modals.create_assessment.step_processing_description'),
      });
    }
    configs.push({
      key: 'review',
      label: this.transloco.translate('modals.create_assessment.step_review'),
    });
    return configs;
  });

  // Inputs
  isVisible = input<boolean>(false);
  isCreating = input<boolean>(false);
  creationNeedsRecovery = input(false);
  successMessage = input<string | null>(null);
  /** Relative editor URL of the created assessment, linked from the success view. */
  successOpenUrl = input<string | null>(null);
  orgId = input<string | null>(null);
  pendingOrgName = input<string | null>(null);
  /** VER-377 "Start a full TIA instead" — opens straight on the vendor
   *  step for a known vendor + country (+ activity), replacing the
   *  record-only TIA `supersedesLinkId`. Read once, when the modal opens. */
  prefill = input<CreateAssessmentPrefill | null>(null);
  /** VER-380 — follow-up link under the error banner (e.g. "View
   *  existing assessment" when the start 409s on an open TIA). */
  errorLink = input<CreateAssessmentErrorLink | null>(null);
  errorMessage = input<string | null>(null);
  /** Whether {@link errorMessage} is a refusal rather than a failure —
   *  set by `create-assessment-handler`, which owns the POST. */
  errorMessageIsDenial = input<boolean>(false);

  /**
   * Which banner tone the shared error slot should wear.
   *
   * The slot renders `errorMessage() || labelCreationError()`, and only the
   * first of those carries a denial flag — a failed label create is its
   * own axis and keeps the warning notice regardless.
   */
  protected readonly bannerIsDenial = computed(
    () => !!this.visibleErrorMessage() && this.errorMessageIsDenial()
  );

  /**
   * The create failure shown as Huxley's error view, like the document wizard,
   * including an unconfirmed create (the footer's Retry recovers it). Refusals and
   * failures that carry a link keep the banner: the amber lock and the link are not
   * outcomes of "creating failed".
   */
  protected readonly createFailed = computed(() =>
    this.errorMessageIsDenial() || this.errorLink() ? null : this.visibleErrorMessage()
  );

  /** Whatever the banner slot shows: the create error it owns, else a label failure. */
  protected readonly bannerMessage = computed(
    () => (this.createFailed() ? null : this.visibleErrorMessage()) || this.labelCreationError()
  );

  /** Step the current `errorMessage` was raised on. A create error
   *  describes that attempt — once the user moves to another step (e.g.
   *  Back to pick a different vendor) it no longer applies, so the banner
   *  hides until the next attempt raises a fresh one (VER-380 follow-up). */
  private readonly _errorStep = signal<AssessmentModalStep | null>(null);
  protected readonly visibleErrorMessage = computed(() => {
    const message = this.errorMessage();
    return message && this._errorStep() === this._currentStep() ? message : null;
  });

  // Outputs
  confirmed = output<CreateAssessmentModalConfirmEvent>();
  cancelled = output<void>();

  // Assessment name
  assessmentName = signal<string>('');

  // Language for AI recommendations — defaults to the org's configured content language
  assessmentLanguage = signal<EntityLanguage>(this.stateService.orgDefaultContentLanguage());

  /**
   * Flips `true` the first time the user visits the language step and
   * picks a value (any value, even the current default). Auto-sync to
   * a linked PA's language only fires while this is `false` — respect
   * explicit user choices, only default when the user hasn't looked.
   *
   * Reset alongside `assessmentLanguage` in `open()` / `close()` so a
   * fresh modal instance starts unlatched.
   */
  private readonly _languageManuallySet = signal(false);

  /**
   * Language of the currently-linked processing activity, if any.
   * Drives:
   *   1. The auto-default on the language step (see `onConfirmed`'s
   *      `'link'` branch — sync language to PA when the user hasn't
   *      manually chosen);
   *   2. The mismatch hint on the language + review steps.
   */
  protected readonly linkedPaLanguage = computed<EntityLanguage | null>(() => {
    if (!this._isLinkedToPa()) return null;
    const id = this._selectedActivityId();
    if (!id) return null;
    const pa = this._availableActivities().find(a => a.activity_id === id);
    return (pa?.language as EntityLanguage | undefined) ?? null;
  });

  /**
   * True when the user's chosen language for this assessment differs
   * from the linked PA's language. Used to render a subtle inline
   * hint on the language + review steps — non-blocking; the user can
   * still submit. If the user hasn't picked a language yet, the auto-
   * default logic keeps them aligned.
   */
  protected readonly hasLanguageMismatch = computed<boolean>(() => {
    const paLang = this.linkedPaLanguage();
    return paLang !== null && paLang !== this.assessmentLanguage();
  });

  /**
   * View-model for the mismatch hint — pre-computes PA name and both
   * uppercased language labels so the template consumes baked strings
   * (no `| uppercase` pipe, no per-render helper calls). `null` when
   * there's no mismatch so the template can just `@if (...as m)`.
   */
  protected readonly languageMismatchDetail = computed(() => {
    if (!this.hasLanguageMismatch()) return null;
    const id = this._selectedActivityId();
    const pa = this._availableActivities().find(a => a.activity_id === id);
    if (!pa) return null;
    return {
      paName: pa.name,
      paLanguage: (pa.language as EntityLanguage).toUpperCase(),
      chosenLanguage: this.assessmentLanguage().toUpperCase(),
    };
  });

  // Context documents state
  contextDocuments = signal<ContextDocument[]>([]);
  /** Stable for uploads during one create flow; rotated when the modal reopens. */
  readonly draftSourceOwnerId = signal(crypto.randomUUID());
  selectedContextDocIds = signal<Set<string>>(new Set());
  readonly contextDocSelectionLimitReached = signal(false);
  /** Search query for the Context-step panel-picker (filters visible rows). */
  readonly contextDocSearch = signal('');
  isLoadingContextDocs = signal<boolean>(false);
  readonly contextDocsLoadError = signal<string | null>(null);
  showUploadModal = signal<boolean>(false);
  private contextDocsLoadRequest = 0;
  private contextDocsLoadSub: Subscription | null = null;

  /** For managers: API path org is the manager org; customer org is the target.
   *  For non-managers: both resolve to the same org. */
  readonly uploadCallerOrgId = computed(() => {
    if (this.stateService.isManagerOrSuperManager()) {
      return this.stateService.selectedOrgId() ?? this.orgId() ?? '';
    }
    return this.orgId() ?? '';
  });
  readonly uploadCustomerOrgId = computed<string | null>(() => {
    if (this.stateService.isManagerOrSuperManager()) {
      return this.orgId() ?? null;
    }
    return null;
  });

  readonly formatFileSize = (bytes: number): string => {
    if (!bytes || bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  };

  // Customer org selection (only for standard orgs)
  isForMyself = signal<boolean>(true); // Default to "for myself"
  customerOrgName = signal<string>('');

  // Label selection state
  availableLabels = signal<LabelResponse[]>([]);
  selectedLabels = signal<LabelResponse[]>([]);
  labelInput = signal<string>('');

  // Cached unfiltered labels for restoring after search
  private cachedLabels = signal<LabelResponse[]>([]);
  private cachedTotalLabels = signal<number>(0);
  private cachedCurrentPage = signal<number>(1);
  private isSearching = signal<boolean>(false);

  // Pagination state
  currentPage = signal<number>(1);
  totalLabels = signal<number>(0);
  isLoadingLabels = signal<boolean>(false);
  limit = 10;

  /** True while the labels autocomplete panel is open — used to avoid
   *  the Enter-to-create handler fighting Material's option pick. */
  private readonly labelAutocompleteOpen = signal(false);

  // Scroll listener reference for cleanup
  private scrollListener: ((event: Event) => void) | null = null;
  private scrollListenerPanel: Element | null = null;

  // Debounce timer for search
  private searchDebounceTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly SEARCH_DEBOUNCE_MS = 300;

  // Label validation state
  isCheckingLabel = signal<boolean>(false);
  labelExists = signal<boolean>(false);
  hasCheckedLabel = signal<boolean>(false); // Track if check has been performed
  labelCreationError = signal<string | null>(null);
  private checkDebounceTimer: ReturnType<typeof setTimeout> | null = null;

  readonly hasMoreLabels = computed(() => this.availableLabels().length < this.totalLabels());

  readonly labelLimitReached = computed(() => {
    const subscription = this.stateService.subscription();
    if (!subscription || subscription.status !== 'active') return false;
    const plans = this.stateService.productPlans();
    const plan = plans[subscription.plan_id];
    if (!plan || plan.labelsLimit === null) return false;
    return this.cachedTotalLabels() >= plan.labelsLimit;
  });

  readonly labelLimitHint = computed(() => {
    const subscription = this.stateService.subscription();
    if (!subscription || subscription.status !== 'active') return null;
    const plans = this.stateService.productPlans();
    const plan = plans[subscription.plan_id];
    if (!plan || plan.labelsLimit === null) return null;
    if (this.cachedTotalLabels() < plan.labelsLimit) return null;
    return `Label limit reached. ${plan.name.charAt(0) + plan.name.slice(1).toLowerCase()} plan allows up to ${plan.labelsLimit} labels.`;
  });
  readonly filteredLabels = computed(() => {
    const selected = this.selectedLabels();

    // Filter out selected labels by ID
    return this.availableLabels().filter(
      label => !selected.some(s => s.label_id === label.label_id)
    );
  });

  // Check if user is standard org (not manager)
  readonly isStandardOrg = computed(() => !this.stateService.isManager());

  // Price is no longer surfaced on the review step. Assessments are free
  // by default on every non-CUSTOM plan, so the row read "Free" for
  // almost every org — a cost question raised at the moment of commitment
  // where there was no cost to answer for. Per-assessment pricing for
  // CUSTOM plans still shows on the billing route, which is where a user
  // goes to reason about spend.

  // Org logo URL for org-display cards
  readonly orgLogoUrl = computed(() => {
    const id = this.orgId() || this.stateService.selectedOrgId();
    if (!id) return null;
    const org = this.stateService.getOrganizationById(id);
    return org?.logo_url ?? null;
  });

  // Display name for review step
  readonly displayOrgName = computed(() => {
    if (this.isStandardOrg() && !this.isForMyself()) {
      return this.customerOrgName() || 'Not specified';
    }
    return this.pendingOrgName() || 'Selected organization';
  });

  // Whether the modal is in a terminal state (creating or success) — hides wizard chrome
  readonly isTerminalState = computed(() => this.isCreating() || !!this.successMessage());

  // Modal configuration — per-step buttons. Title reflects the active
  // plugin's label so a DPIA-selected modal reads "New DPIA" rather
  // than the generic prefix + hard-coded AI-Risk label.
  readonly modalConfig = computed<ModalConfig>(() => {
    this.translationVersion();
    const step = this._currentStep();
    const terminal = this.isTerminalState();
    const prefix = this.transloco.translate('modals.create_assessment.modal_title_prefix');
    const plugin = this.activePlugin();
    // Selected type ⇒ dynamic title; type-step ⇒ generic "New Assessment"
    // (the user hasn't chosen yet, so committing to a label would be wrong).
    const typeLabel =
      step === 'type' && this.registeredTypes.length > 1
        ? this.transloco.translate('modals.create_assessment.title_generic_new_assessment')
        : this.transloco.translate(plugin.labelKey);
    const base = {
      title: `${prefix} ${typeLabel}`,
      size: 'medium' as const,
      showCloseButton: !this.isCreating(),
      showFooter: !this.isCreating(),
      showLogo: true,
      showManagedCustomerContext: !terminal,
    };

    // The success view, which links to the created record, stays until the user is done with it.
    if (this.successMessage()) {
      return {
        ...base,
        primaryButtonText: this.transloco.translate('common.close'),
      };
    }

    switch (step) {
      case 'type':
        return {
          ...base,
          primaryButtonText: this.transloco.translate('modals.create_assessment.primary_next'),
          secondaryButtonText: this.transloco.translate(
            'modals.create_assessment.secondary_cancel'
          ),
          primaryButtonDisabled: this._selectedType() === null,
        };
      case 'vendor':
        return {
          ...base,
          primaryButtonText: this.transloco.translate('modals.create_assessment.primary_next'),
          secondaryButtonText: this.transloco.translate(
            this.registeredTypes.length > 1
              ? 'modals.create_assessment.secondary_back'
              : 'modals.create_assessment.secondary_cancel'
          ),
          primaryButtonDisabled: !this.canProceedFromVendorStep(),
        };
      case 'link':
        return {
          ...base,
          primaryButtonText: this.transloco.translate('modals.create_assessment.primary_next'),
          secondaryButtonText: this.transloco.translate(
            this.registeredTypes.length > 1
              ? 'modals.create_assessment.secondary_back'
              : 'modals.create_assessment.secondary_cancel'
          ),
          // Linked mode requires a PA choice; standalone requires nothing.
          // A clash with an open TIA (VER-380) blocks too — the start would 409.
          primaryButtonDisabled:
            (this._isLinkedToPa() && !this._selectedActivityId()) ||
            !!this.duplicateOpenTia() ||
            this.dpiaLinkBlocked(),
        };
      case 'language':
        // `assessmentLanguage` is signal-initialised to the org default,
        // never nullable, so Next is always enabled. Back label depends
        // on whether type / link steps preceded us. Include the shared
        // `primaryButtonDisabled` key so the union type inferred by
        // TypeScript is consistent across every switch branch — the
        // template's `modalConfig().primaryButtonDisabled` binding
        // then narrows cleanly.
        return {
          ...base,
          primaryButtonText: this.transloco.translate('modals.create_assessment.primary_next'),
          secondaryButtonText: this.transloco.translate(
            this.showLinkStep() || this.registeredTypes.length > 1
              ? 'modals.create_assessment.secondary_back'
              : 'modals.create_assessment.secondary_cancel'
          ),
          primaryButtonDisabled: false,
        };
      case 'details':
        return {
          ...base,
          primaryButtonText: this.transloco.translate('modals.create_assessment.primary_next'),
          // Back path: link → type (if type step present); or details → type; or details → cancel.
          secondaryButtonText: this.transloco.translate(
            this.showLinkStep() || this.registeredTypes.length > 1
              ? 'modals.create_assessment.secondary_back'
              : 'modals.create_assessment.secondary_cancel'
          ),
          primaryButtonDisabled: !this.assessmentName().trim(),
        };
      case 'context-docs':
        return {
          ...base,
          primaryButtonText: this.transloco.translate('modals.create_assessment.primary_next'),
          secondaryButtonText: this.transloco.translate('modals.create_assessment.secondary_back'),
          primaryButtonDisabled: this.hasExtractingSelection(),
        };
      case 'derogations': {
        // VER-383 — with a derogation claimed: Back / Record derogation /
        // Continue with TIA. "Record" isn't offered when replacing a
        // recorded derogation ("Create TIA" is always a full TIA).
        const claim = this.hasDerogationClaim();
        return {
          ...base,
          primaryButtonText: this.transloco.translate(
            claim ? 'tia.derogations.continue_with_tia' : 'modals.create_assessment.primary_next'
          ),
          secondaryButtonText: this.transloco.translate('modals.create_assessment.secondary_back'),
          // An unconfirmed record-only create is retried from the button that sent it, so Continue is off meanwhile.
          tertiaryButtonText:
            claim && this.canRecordOnly()
              ? this.retryOr('tia.derogations.record_derogation')
              : undefined,
          tertiaryButtonDisabled:
            !this.canProceedFromDerogationsStep() ||
            this.isCreating() ||
            this.stateService.assessmentCreationInFlight(),
          primaryButtonDisabled:
            !this.canProceedFromDerogationsStep() || this.creationNeedsRecovery(),
        };
      }
      case 'review':
        return {
          ...base,
          primaryButtonText: this.retryOr(
            this.isRecordOnly()
              ? 'tia.wizard.primary_record_derogation'
              : 'modals.create_assessment.primary_create'
          ),
          secondaryButtonText: this.creationNeedsRecovery()
            ? undefined
            : this.transloco.translate('modals.create_assessment.secondary_back'),
          // Creation, activity availability, extracted documents and reviewed answers must all be ready.
          primaryButtonDisabled:
            this.isCreating() ||
            this.dpiaLinkBlocked() ||
            !!this.duplicateOpenTia() ||
            this.stateService.assessmentCreationInFlight() ||
            this.hasExtractingSelection() ||
            (this.showPreQuestionnaireStep() &&
              (!this.canConfirmPreQuestionnaire() || !this._descriptionReviewed())),
        };
      case 'processing-description':
        return {
          ...base,
          primaryButtonText: this.transloco.translate('modals.create_assessment.primary_next'),
          secondaryButtonText: this.transloco.translate('modals.create_assessment.secondary_back'),
          primaryButtonDisabled: !this.canConfirmPreQuestionnaire(),
        };
    }
  });

  private wasVisible = false;
  private lastOrgId: string | null = null;

  constructor() {
    effect(() => {
      if (this.errorMessage()) {
        this._errorStep.set(untracked(() => this._currentStep()));
        if (!this.creationNeedsRecovery()) untracked(() => this.checkActivity());
      }
    });

    this.transloco.langChanges$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.translationVersion.update(v => v + 1));

    effect(() => {
      const isCurrentlyVisible = this.isVisible();
      const currentOrgId = this.orgId();

      // Reset state when modal closes — deferred so the user never sees step 1 flash
      if (!isCurrentlyVisible && this.wasVisible) {
        this.resetState();
      }

      if (isCurrentlyVisible && this.wasVisible && currentOrgId !== this.lastOrgId) {
        this.resetState();
        this.wasVisible = false;
      }

      // Initialise the wizard when the modal becomes visible. Signal
      // defaults can't do this because `availableTypes()` depends on
      // the state store's feature flags, which aren't stable until
      // after component construction. Multi-type orgs land on `type`
      // (picker); single-type orgs start with the assessment language.
      // Also clear any residual selection from a prior instance so the
      // user always sees an unselected picker on first open.
      if (isCurrentlyVisible && !this.wasVisible) {
        this.draftSourceOwnerId.set(crypto.randomUUID());
        this._selectedType.set(null);
        const initialStep = this.registeredTypes.length > 1 ? 'type' : 'language';
        this._currentStep.set(initialStep);
        this._maxReachedStep.set(initialStep);
        // Reset PA-picker to defaults on each fresh open (FE-G).
        this._isLinkedToPa.set(true);
        this._selectedActivityId.set(null);
        this._availableActivities.set([]);
        // Unlatch the "user picked language explicitly" flag so the
        // PA-linked auto-default kicks in for the fresh flow.
        this._languageManuallySet.set(false);
        // Reset vendor-picker + derogations state (VER-331) on each fresh
        // open — mirrors the PA-picker reset above so a re-open against a
        // different org never shows a stale vendor list.
        this._vendorList.set([]);
        this._selectedVendorId.set(null);
        this._gatePreview.set(null);
        this._gatePreviewError.set(null);
        this._assessAnyway.set(false);
        this.resetDerogations();
        this._supersedesLinkId.set(null);
        const prefill = untracked(() => this.prefill());
        if (prefill) untracked(() => this.applyPrefill(prefill));
      }

      // Single-type builds never show the type picker, so `selectType`
      // never fires — warm the definition on open instead.
      if (isCurrentlyVisible && !this.wasVisible && this.registeredTypes.length === 1) {
        this.loadPreQuestionnaireDefinition();
      }

      // Load labels when modal becomes visible OR when org changes while visible
      if (
        isCurrentlyVisible &&
        (isCurrentlyVisible !== this.wasVisible || currentOrgId !== this.lastOrgId)
      ) {
        console.debug('🏷️ CreateAssessmentModal: Triggering label load', {
          orgId: currentOrgId,
          isVisible: isCurrentlyVisible,
        });
        this.loadLabels(1, true);
      }
      this.wasVisible = isCurrentlyVisible;
      this.lastOrgId = currentOrgId;
    });

    effect(() => {
      const visible = this.isVisible();
      const enabled = this.showPreQuestionnaireStep();
      const version = this._questionnaireVersion();
      const orgId = this.orgId();
      const request = {
        assessment_type:
          this.selectedType() ??
          this.registeredTypes[0]?.type ??
          AssessmentTypeEnum.AI_RISK_ASSESSMENT,
        activity_id: this.isLinkedToPa() ? this.selectedActivityId() : null,
        context_document_ids: [...this.selectedContextDocIds()].sort(),
        ...(this.selectedContextDocIds().size
          ? { context_source_owner: { kind: 'session' as const, id: this.draftSourceOwnerId() } }
          : {}),
        language: this.assessmentLanguage().toLowerCase(),
        questionnaire_version: version ?? '',
      };
      const step = this.currentStep();
      untracked(() => {
        if (!visible || !enabled || !version || !orgId) {
          this.descriptionSuggestion.reset();
          return;
        }
        if (this.descriptionSuggestion.setContext(orgId, request)) {
          this._descriptionReviewed.set(false);
          this._descriptionEdits.set({});
          this._dismissedDescriptions.set(new Set());
        }
        if (step === 'processing-description' || step === 'review')
          this.descriptionSuggestion.load();
      });
    });
  }

  private loadLabels(page: number = 1, force: boolean = false): void {
    const currentOrgId = this.orgId() || this.stateService.selectedOrgId();
    if (!currentOrgId || this.isLoadingLabels()) {
      console.debug('🏷️ CreateAssessmentModal: Skipping label load', {
        currentOrgId,
        isLoading: this.isLoadingLabels(),
      });
      return;
    }

    // Prevent reloading page 1 if we already have labels (unless it's an explicit refresh or we are searching)
    const searchQuery = this.labelInput().trim();
    if (page === 1 && this.availableLabels().length > 0 && !force && !searchQuery) return;

    // Prevent loading beyond totals
    if (page > 1 && !this.hasMoreLabels()) return;

    console.debug('🏷️ CreateAssessmentModal: Fetching labels for org:', currentOrgId);
    this.isLoadingLabels.set(true);
    this.orgApi.getLabels(currentOrgId, page, this.limit, searchQuery).subscribe({
      next: response => {
        if (page === 1) {
          this.availableLabels.set(response.labels);
          // If not searching, update the cache
          if (!searchQuery) {
            this.cachedLabels.set(response.labels);
            this.cachedTotalLabels.set(response.total);
            this.cachedCurrentPage.set(page);
            this.isSearching.set(false);
          } else {
            this.isSearching.set(true);
          }
        } else {
          // Append labels, filtering out any that might already be in availableLabels
          const existingIds = new Set(this.availableLabels().map(l => l.label_id));
          const newLabels = response.labels.filter(l => !existingIds.has(l.label_id));
          const updatedLabels = [...this.availableLabels(), ...newLabels];
          this.availableLabels.set(updatedLabels);

          if (!searchQuery) {
            this.cachedLabels.set(updatedLabels);
            this.cachedCurrentPage.set(page);
          }
        }
        this.totalLabels.set(response.total);
        this.currentPage.set(page);
        this.isLoadingLabels.set(false);
      },
      error: err => {
        console.error('❌ Failed to load labels:', err);
        this.isLoadingLabels.set(false);
      },
    });
  }

  onAutocompleteOpened(): void {
    this.labelAutocompleteOpen.set(true);

    this.removeScrollListener();
    const panel = this.labelAutocomplete()?.panel?.nativeElement;
    if (panel) {
      this.scrollListener = this.onScroll.bind(this);
      this.scrollListenerPanel = panel;
      panel.addEventListener('scroll', this.scrollListener);
    }
  }

  onAutocompleteClosed(): void {
    this.labelAutocompleteOpen.set(false);
    this.removeScrollListener();
  }

  private removeScrollListener(): void {
    if (this.scrollListener && this.scrollListenerPanel) {
      this.scrollListenerPanel.removeEventListener('scroll', this.scrollListener);
      this.scrollListener = null;
      this.scrollListenerPanel = null;
    }
  }

  ngOnDestroy(): void {
    this.removeScrollListener();
    this.extractionPollSub?.unsubscribe();
    if (this.searchDebounceTimer) {
      clearTimeout(this.searchDebounceTimer);
    }
    if (this.checkDebounceTimer) {
      clearTimeout(this.checkDebounceTimer);
    }
  }

  private onScroll(event: Event): void {
    const target = event.target as HTMLElement;
    const threshold = 10;

    // Only fetch more if we are not at the top (i.e. we have enough content to scroll)
    // and we hit the bottom threshold.
    const isScrollable = target.scrollHeight > target.clientHeight;
    const atBottom = target.scrollTop + target.clientHeight >= target.scrollHeight - threshold;

    if (isScrollable && atBottom && this.hasMoreLabels() && !this.isLoadingLabels()) {
      this.loadLabels(this.currentPage() + 1);
    }
  }

  // Label management methods
  onLabelInputChange(event: Event): void {
    const target = event.target as HTMLInputElement;
    this.labelInput.set(target.value);

    // Clear existing debounce timers FIRST to prevent race conditions
    if (this.searchDebounceTimer) {
      clearTimeout(this.searchDebounceTimer);
      this.searchDebounceTimer = null;
    }
    if (this.checkDebounceTimer) {
      clearTimeout(this.checkDebounceTimer);
      this.checkDebounceTimer = null;
    }

    const input = target.value.trim();

    // Clear creation error on any new input
    this.labelCreationError.set(null);

    // Reset validation state if input is empty
    if (!input) {
      this.isCheckingLabel.set(false);
      this.labelExists.set(false);
      this.hasCheckedLabel.set(false);

      // If we were searching, reload base labels (without search query)
      if (this.isSearching()) {
        this.currentPage.set(1);
        this.isSearching.set(false);
        this.loadLabels(1, true); // Force reload without search query
      }
      // Return early - don't set new timers for empty input
      return;
    }

    // Set new debounce timer for search/filter (only if input is not empty)
    this.searchDebounceTimer = setTimeout(() => {
      this.currentPage.set(1);
      // availableLabels and totalLabels are reset by loadLabels(1) if we pass force or if it's a search
      this.loadLabels(1, true);
    }, this.SEARCH_DEBOUNCE_MS);

    // Set new debounce timer for label existence check
    this.checkDebounceTimer = setTimeout(() => {
      const currentOrgId = this.orgId() || this.stateService.selectedOrgId();
      if (currentOrgId) {
        this.isCheckingLabel.set(true);
        this.hasCheckedLabel.set(false);
        this.orgApi.checkLabelExists(currentOrgId, input).subscribe({
          next: response => {
            // valid: false means label exists (block Enter)
            // valid: true means label doesn't exist (allow Enter)
            this.labelExists.set(!response.valid);
            this.isCheckingLabel.set(false);
            this.hasCheckedLabel.set(true);
          },
          error: err => {
            console.error('❌ Failed to check label existence:', err);
            this.isCheckingLabel.set(false);
            this.labelExists.set(false);
            this.hasCheckedLabel.set(true);
          },
        });
      }
    }, this.SEARCH_DEBOUNCE_MS);
  }

  onAutocompleteLabelSelected(labelId: string | null | undefined): void {
    if (labelId === this.loadMoreLabelValue) {
      if (this.hasMoreLabels() && !this.isLoadingLabels()) this.loadLabels(this.currentPage() + 1);
      const input = this.labelInputEl()?.nativeElement;
      if (input) input.value = this.labelInput();
      afterNextRender(
        () => {
          if (this.labelInputEl()?.nativeElement.isConnected)
            this.labelAutocompleteTrigger()?.openPanel();
        },
        { injector: this.injector }
      );
      return;
    }
    if (!labelId || typeof labelId !== 'string') {
      return;
    }
    const label = this.findLabelById(labelId);
    if (label) {
      this.onLabelSelected(label);
    }
  }

  onLabelSelected(label: LabelResponse): void {
    if (!label?.label_id) {
      return;
    }
    if (!this.selectedLabels().some(s => s.label_id === label.label_id)) {
      this.selectedLabels.set([...this.selectedLabels(), label]);
    }
    this.clearLabelInput();

    // If we were searching, reload base labels (without search query)
    if (this.isSearching()) {
      this.currentPage.set(1);
      this.isSearching.set(false);
      this.loadLabels(1, true);
    }
  }

  private findLabelById(labelId: string): LabelResponse | undefined {
    return (
      this.availableLabels().find(l => l.label_id === labelId) ??
      this.cachedLabels().find(l => l.label_id === labelId) ??
      this.selectedLabels().find(l => l.label_id === labelId)
    );
  }

  /** Case-insensitive exact name match among labels not yet selected. */
  private findAvailableLabelByName(name: string): LabelResponse | undefined {
    const normalized = name.trim().toLowerCase();
    if (!normalized) {
      return undefined;
    }
    return this.filteredLabels().find(l => l.label_name.trim().toLowerCase() === normalized);
  }

  private clearLabelInput(): void {
    this.labelInput.set('');
    this.isCheckingLabel.set(false);
    this.labelExists.set(false);
    this.hasCheckedLabel.set(false);
    const el = this.labelInputEl()?.nativeElement;
    if (el) {
      el.value = '';
    }
  }

  onLabelInputKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter') {
      // Let Material pick the highlighted autocomplete option.
      if (this.labelAutocompleteOpen()) {
        return;
      }

      event.preventDefault();

      const input = this.labelInput().trim();
      if (!input) {
        return;
      }

      // Block Enter if label limit is reached
      if (this.labelLimitReached()) {
        return;
      }

      // Block Enter if we're still checking the label
      if (this.isCheckingLabel()) {
        return;
      }

      // Block Enter if check hasn't been performed yet
      if (!this.hasCheckedLabel()) {
        return;
      }

      // Existing label — add the match instead of trying to create a duplicate.
      if (this.labelExists()) {
        const existing = this.findAvailableLabelByName(input);
        if (existing) {
          this.onLabelSelected(existing);
        }
        return;
      }

      // At this point, check API has completed and label doesn't exist
      // Clear the debounce timers to avoid API calls after creation
      if (this.searchDebounceTimer) {
        clearTimeout(this.searchDebounceTimer);
        this.searchDebounceTimer = null;
      }
      if (this.checkDebounceTimer) {
        clearTimeout(this.checkDebounceTimer);
        this.checkDebounceTimer = null;
      }

      // Create new label with the input text
      const currentOrgId = this.orgId() || this.stateService.selectedOrgId();
      if (currentOrgId) {
        this.orgApi.createLabel(currentOrgId, { label_name: input }).subscribe({
          next: label => {
            console.debug('✅ Created new label:', label);

            // Add to both cache and availableLabels to keep them in sync
            this.cachedLabels.set([...this.cachedLabels(), label]);
            this.cachedTotalLabels.set(this.cachedTotalLabels() + 1);
            this.totalLabels.set(this.totalLabels() + 1);
            this.availableLabels.set([...this.availableLabels(), label]);

            // Select the newly created label (clears input and restores cache if searching)
            this.onLabelSelected(label);
          },
          error: err => {
            console.error('❌ Failed to create label:', err);
            const message = err?.message || 'Failed to create label';
            this.labelCreationError.set(message);
          },
        });
      }
    }
  }

  removeLabel(label: LabelResponse): void {
    this.selectedLabels.set(this.selectedLabels().filter(l => l.label_id !== label.label_id));
    this.clearLabelInput();
  }

  onSegmentClick(isForMyself: boolean): void {
    this.isForMyself.set(isForMyself);
    // Clear customer org name when switching back to "for myself"
    if (isForMyself) {
      this.customerOrgName.set('');
    }
  }

  onConfirmed(): void {
    if (this.successMessage()) {
      this.cancelled.emit();
      return;
    }
    switch (this._currentStep()) {
      case 'type':
        if (this._selectedType() === null) return;
        // Vendor-shaped types (TIA) go to the vendor picker first;
        // DPIA-shaped types dive into the link picker; others jump
        // straight to language.
        if (this.showVendorStep()) {
          this.loadVendorsIfNeeded();
          this.setStep('vendor');
        } else if (this.showLinkStep()) {
          this.loadActivitiesIfNeeded();
          this.setStep('link');
        } else {
          this.setStep('language');
        }
        break;
      case 'vendor': {
        if (!this.canProceedFromVendorStep()) return;
        const vendor = this.selectedVendor();
        const vendorName = vendor?.global_vendor?.name;
        if (vendorName && !this.assessmentName().trim()) {
          this.assessmentName.set(
            `${this.transloco.translate(this.activePlugin().labelKey)} — ${vendorName}`
          );
        }
        // The Link step is optional for vendor-shaped types — default to
        // "not linked" (ticket: "Link processing activity (optional)"),
        // unlike DPIA's mandatory-link default.
        if (this.showLinkStep()) {
          // A prefilled replacement keeps the record's own activity link.
          if (!this._supersedesLinkId()) this._isLinkedToPa.set(this.paLinkRequired());
          this.loadActivitiesIfNeeded();
          this.setStep('link');
        } else {
          this.setStep('language');
        }
        break;
      }
      case 'link':
        if (this._isLinkedToPa() && !this._selectedActivityId()) return;
        if (this.duplicateOpenTia() || this.dpiaLinkBlocked()) return;
        // Prefill the assessment name with "DPIA — {PA name}" so the
        // reviewer sees the linkage in the details step. Editable there.
        if (this._isLinkedToPa()) {
          const pa = this._availableActivities().find(
            a => a.activity_id === this._selectedActivityId()
          );
          if (pa && !this.assessmentName().trim()) {
            this.assessmentName.set(
              `${this.transloco.translate('assessment_types.dpia_label')} — ${pa.name}`
            );
          }
          // Inherit the linked PA's content language as the assessment's
          // default — but only when the user hasn't already picked a
          // language explicitly. This keeps a linked DPIA aligned with
          // its PA's language by default without stealing an intentional
          // override made earlier in the session.
          const paLang = pa?.language as EntityLanguage | undefined;
          if (paLang && !this._languageManuallySet()) {
            this.assessmentLanguage.set(paLang);
          }
        }
        this.setStep('language');
        break;
      case 'language':
        // Latch: the user has now seen + committed to a language choice.
        // Any subsequent PA change on the link step will no longer
        // auto-override this pick.
        this._languageManuallySet.set(true);
        this.setStep('details');
        break;
      case 'details': {
        const name = this.assessmentName().trim();
        if (!name) return;
        this.loadContextDocuments();
        this.setStep('context-docs');
        break;
      }
      case 'context-docs':
        if (this.hasExtractingSelection()) return;
        if (this.showDerogationsStep()) {
          this.setStep('derogations');
        } else {
          this.setStep(this.showPreQuestionnaireStep() ? 'processing-description' : 'review');
        }
        break;
      case 'derogations':
        // Primary = "Continue with TIA" when a derogation is claimed.
        if (!this.canProceedFromDerogationsStep() || this.creationNeedsRecovery()) return;
        this._derogationOutcome.set(this.hasDerogationClaim() ? 'full' : null);
        this.setStep('review');
        break;
      case 'review':
        // An unconfirmed create is retried as it was sent: its idempotency key recovers the record the lost
        // response may have created, which the activity re-check would otherwise report as a conflict.
        if (this.creationNeedsRecovery()) {
          this.submitAssessment();
          break;
        }
        if (this.dpiaLinkBlocked()) return;
        if (this.paLinkRequired() && this.isLinkedToPa())
          this.checkActivity(() => this.submitAssessment());
        else this.submitAssessment();
        break;
      case 'processing-description':
        if (!this.canConfirmPreQuestionnaire()) return;
        this._descriptionReviewed.set(true);
        this.setStep('review');
        break;
    }
  }

  /** Label of a create button: Retry while an unconfirmed create is pending, else the step's own ``key``. */
  private retryOr(key: string): string {
    return this.transloco.translate(this.creationNeedsRecovery() ? 'common.retry' : key);
  }

  /** Footer tertiary button — "Record derogation" on the Derogations step
   *  (VER-383): records the claim and finishes; no TIA, no Review step. */
  onTertiary(): void {
    if (this._currentStep() !== 'derogations') return;
    if (!this.canRecordOnly() || !this.hasDerogationClaim()) return;
    if (!this.canProceedFromDerogationsStep()) return;
    this._derogationOutcome.set('record');
    this.submitAssessment();
  }

  /** X button or backdrop — after discard confirmation if dirty. */
  onDismiss(): void {
    // The request is in flight: closing now would orphan a record the user never sees confirmed.
    if (this.isCreating()) return;
    this.cancelled.emit();
  }

  /** Secondary (Back/Cancel) button. */
  onSecondary(): void {
    if (this.creationNeedsRecovery()) return;
    const multiType = this.registeredTypes.length > 1;
    switch (this._currentStep()) {
      case 'type':
        this.cancelled.emit();
        break;
      case 'vendor':
        // Vendor is only shown for vendor-shaped types; back always goes
        // to the type picker (vendor-shaped types always run with
        // registeredTypes.length > 1 today, but guard anyway).
        if (multiType) {
          this._currentStep.set('type');
        } else {
          this.cancelled.emit();
        }
        break;
      case 'link':
        // Link precedes the type/vendor picker for vendor-shaped types;
        // for DPIA-shaped types it's the first step. Back always goes to
        // whichever preceded it when the step is in the flow, else Cancel.
        if (this.showVendorStep()) {
          this._currentStep.set('vendor');
        } else if (multiType) {
          this._currentStep.set('type');
        } else {
          this.cancelled.emit();
        }
        break;
      case 'language':
        // Back path mirrors the forward path: language ← link (PA-scoped
        // or vendor-optional) ← vendor/type ← cancel (single-type non-PA).
        if (this.showLinkStep()) {
          this._currentStep.set('link');
        } else if (this.showVendorStep()) {
          this._currentStep.set('vendor');
        } else if (multiType) {
          this._currentStep.set('type');
        } else {
          this.cancelled.emit();
        }
        break;
      case 'details':
        // Language always precedes details in the new flow.
        this._currentStep.set('language');
        break;
      case 'context-docs':
        this._currentStep.set('details');
        break;
      case 'derogations':
        this._currentStep.set('context-docs');
        break;
      case 'review':
        this._currentStep.set(
          this.showPreQuestionnaireStep()
            ? 'processing-description'
            : this.showDerogationsStep()
              ? 'derogations'
              : 'context-docs'
        );
        break;
      case 'processing-description':
        this._currentStep.set('context-docs');
        break;
    }
  }

  /** Type-step card click handler. */
  selectType(type: AssessmentTypeEnum): void {
    if (this.selectedType() !== type) {
      this.questionnaireDefinitionSub?.unsubscribe();
      this._isLoadingQuestionnaire.set(false);
      this._preQuestionnaireAnswers.set({});
      this._descriptionReviewed.set(false);
      this._questionnaireVersion.set(null);
      this.activitiesSub?.unsubscribe();
      this.activityCheckSub?.unsubscribe();
      this._availableActivities.set([]);
      this._isLoadingActivities.set(false);
      this._selectedActivityId.set(null);
      this.existingDpias.set(new Map());
      this.activityCheck.set('idle');
    }
    this._selectedType.set(type);
    // Warm the questionnaire definition now so the pre-questionnaire
    // step's copy is in hand by the time the user reaches it (several
    // steps later). Cached by the service — a no-op on re-selection.
    this.loadPreQuestionnaireDefinition();
  }

  // ── PA picker (FE-G) ──────────────────────────────────────────────

  /** Radio-group change from linked ↔ standalone. Clears the selection
   *  when switching to standalone so a stale activity_id can't leak. */
  onLinkModeChange(linked: boolean): void {
    this._isLinkedToPa.set(linked);
    if (!linked) {
      this._selectedActivityId.set(null);
    }
    this.checkActivity();
  }

  onActivityPicked(activityId: string | null): void {
    if (activityId && this.paLinkRequired() && this.existingDpias().has(activityId)) return;
    this._selectedActivityId.set(activityId);
    this.checkActivity();
  }

  retryActivityAvailability(): void {
    if (this.activityLoadFailed()) this.refreshActivities();
    else this.checkActivity();
  }

  refreshActivities(): void {
    this.activitiesSub?.unsubscribe();
    this._isLoadingActivities.set(false);
    this._availableActivities.set([]);
    this._selectedActivityId.set(null);
    this.questionnaireDefinitionSub?.unsubscribe();
    this._isLoadingQuestionnaire.set(false);
    this.activityCheckSub?.unsubscribe();
    this.activityCheck.set('idle');
    this.loadActivitiesIfNeeded();
  }

  private loadActivitiesIfNeeded(): void {
    if (this._availableActivities().length > 0 || this._isLoadingActivities()) return;
    const orgId = this.orgId() || this.stateService.selectedOrgId();
    if (!orgId) return;
    this.activitiesSub?.unsubscribe();
    this._isLoadingActivities.set(true);
    this.activityLoadFailed.set(false);
    const checkDpia = this.paLinkRequired();
    this.activitiesSub = this.vendorApi
      .listProcessingActivities(orgId)
      .pipe(
        map(activities => activities.filter(a => a.state !== 'RETIRED' && !a.archived_at)),
        switchMap(activities => {
          if (!checkDpia || activities.length === 0) return of({ activities, links: [] });
          // Reuse the tenant-scoped link contract, with bounded concurrency.
          return from(activities).pipe(
            mergeMap(activity => this.readOpenDpia(orgId, activity.activity_id), 4),
            toArray(),
            map(links => ({ activities, links }))
          );
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: ({ activities, links }) => {
          this._availableActivities.set(activities);
          this.existingDpias.set(
            new Map(links.flatMap(link => (link ? [[link.activity_id, link] as const] : [])))
          );
          this._isLoadingActivities.set(false);
          const eligible = activities.filter(a => !this.existingDpias().has(a.activity_id));
          if (eligible.length === 1 && this._selectedActivityId() === null) {
            this.onActivityPicked(eligible[0].activity_id);
          }
        },
        error: () => {
          this.activityLoadFailed.set(true);
          this._isLoadingActivities.set(false);
        },
      });
  }

  readonly hasUnsavedWork = computed(
    () =>
      this.assessmentName().trim().length > 0 ||
      this.selectedLabels().length > 0 ||
      Object.values(this._preQuestionnaireAnswers()).some(answer => !!answer.trim())
  );

  // ── Context documents ──────────────────────────────────────────────

  loadContextDocuments(): void {
    const targetOrgId = this.orgId() || this.stateService.selectedOrgId();
    if (!targetOrgId) return;

    this.contextDocsLoadSub?.unsubscribe();
    const request = ++this.contextDocsLoadRequest;
    const ownerId = this.draftSourceOwnerId();
    const isCurrent = () =>
      request === this.contextDocsLoadRequest &&
      targetOrgId === (this.orgId() || this.stateService.selectedOrgId()) &&
      ownerId === this.draftSourceOwnerId();

    // `context_document_handler.handle_get` is strictly path-scoped — pass
    // the actual target org (customer or self) directly; it no longer
    // reads a `customer_org_id` filter (dropped to close a cross-customer
    // data leak, see commit 9993c021).
    this.isLoadingContextDocs.set(true);
    this.contextDocsLoadError.set(null);
    this.contextDocsLoadSub = this.orgApi
      .getContextDocuments(targetOrgId, {
        source_owner_kind: 'session',
        source_owner_id: ownerId,
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: response => {
          if (!isCurrent()) return;
          // Hide only docs whose extraction FAILED — they carry no text, so
          // they can't ground anything. Docs still being extracted stay
          // listed (with a status chip) rather than vanishing: a user who
          // just uploaded one would otherwise think the upload was lost.
          // Create is gated until they settle — see `hasExtractingSelection`.
          const docs = (response.context_documents || []).filter(
            d => d.extraction_status !== 'failed'
          );
          this.contextDocuments.set(docs);
          this.isLoadingContextDocs.set(false);
          this.pollSelectedDocExtraction();
        },
        error: err => {
          if (!isCurrent()) return;
          this.contextDocsLoadError.set(
            accessDenialMessage(err, this.transloco) ??
              getUserFriendlyErrorMessage(
                err,
                this.transloco.translate('context_documents.load_failed'),
                this.transloco
              )
          );
          this.isLoadingContextDocs.set(false);
        },
      });
  }

  /**
   * Selected documents whose text extraction hasn't reached a terminal
   * state yet. Freshly uploaded docs land here: `onUploadModalClosed`
   * auto-selects them while crp-context is still extracting.
   *
   * Why this gates Create: extraction is asynchronous, and a document
   * that is still `processing` contributes **no text** to the AI's
   * grounding context. The BE has a safety net (an empty context blob
   * means it doesn't call the agent at all, rather than letting it invent
   * a description) — so the outcome is correct either way, but the user
   * who just attached a document and gets a blank box reads that as the
   * feature not working. Waiting the few seconds out is the better UX.
   */
  readonly extractingSelection = computed<readonly ContextDocument[]>(() => {
    const selected = this.selectedContextDocIds();
    return this.contextDocuments().filter(
      doc => selected.has(doc.context_document_id) && !isContextDocTerminal(doc)
    );
  });

  readonly hasExtractingSelection = computed(() => this.extractingSelection().length > 0);

  /**
   * Baked rows for the context-docs picker. Filtered by
   * {@link contextDocSearch}; selection / icon / size / extraction are
   * precomputed so the template does not allocate per CD cycle.
   */
  readonly contextDocRows = computed<readonly ContextDocRow[]>(() => {
    const selected = this.selectedContextDocIds();
    const q = this.contextDocSearch().toLowerCase().trim();
    const docs = this.contextDocuments();
    const filtered = q
      ? docs.filter(
          d =>
            d.file_name.toLowerCase().includes(q) || (d.description ?? '').toLowerCase().includes(q)
        )
      : docs;
    return filtered.map(doc => {
      const iconConfig = getContextDocIconConfig(doc.file_type);
      return {
        ...doc,
        selected: selected.has(doc.context_document_id),
        icon: iconConfig.icon,
        iconVariant: iconConfig.variant,
        typeLabel: getContextDocFileTypeLabel(doc.file_type),
        sizeLabel: doc.file_size ? this.formatFileSize(doc.file_size) : null,
        extraction: getContextDocExtractionDisplay(doc),
      };
    });
  });

  /** Tri-state for the shared panel-picker select-all header. */
  readonly contextDocsSelectionState = computed(() =>
    deriveSelectionState(
      this.contextDocRows().map(r => r.context_document_id),
      this.selectedContextDocIds()
    )
  );

  /** Watches the attached documents until every selected one reaches a
   *  terminal extraction status, then stops. Restarted whenever a new
   *  upload joins the selection; torn down with the component. */
  private extractionPollSub: Subscription | null = null;

  private pollSelectedDocExtraction(): void {
    const targetOrgId = this.orgId() || this.stateService.selectedOrgId();
    if (!targetOrgId || !this.hasExtractingSelection()) return;

    this.extractionPollSub?.unsubscribe();
    const ownerId = this.draftSourceOwnerId();
    const request = this.contextDocsLoadRequest;
    const isCurrent = () =>
      request === this.contextDocsLoadRequest &&
      targetOrgId === (this.orgId() || this.stateService.selectedOrgId()) &&
      ownerId === this.draftSourceOwnerId();
    const watched = new Set(this.extractingSelection().map(d => d.context_document_id));
    const isDone = (response: ContextDocumentsResponse): boolean =>
      (response.context_documents ?? [])
        .filter(doc => watched.has(doc.context_document_id))
        .every(doc => isContextDocTerminal(doc));

    this.extractionPollSub = this.orgApi
      .pollContextDocuments(
        targetOrgId,
        {
          source_owner_kind: 'session',
          source_owner_id: ownerId,
        },
        isDone,
        { intervalMs: 3_000, maxDurationMs: 180_000 }
      )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: response => {
          if (!isCurrent()) return;
          // Merge fresh extraction state into the rows we already show,
          // keeping any doc the listing hasn't caught up with yet (a
          // just-minted shell row can lag one tick behind).
          const byId = new Map(
            (response.context_documents ?? []).map(doc => [doc.context_document_id, doc])
          );
          this.contextDocuments.update(docs =>
            docs.map(doc => byId.get(doc.context_document_id) ?? doc)
          );
        },
        error: err => {
          if (!isCurrent()) return;
          this.contextDocsLoadError.set(
            accessDenialMessage(err, this.transloco) ??
              getUserFriendlyErrorMessage(
                err,
                this.transloco.translate('context_documents.load_failed'),
                this.transloco
              )
          );
        },
      });
  }

  toggleContextDoc(docId: string): void {
    const current = new Set(this.selectedContextDocIds());
    if (current.has(docId)) {
      current.delete(docId);
      this.contextDocSelectionLimitReached.set(current.size > MAX_CONTEXT_SOURCE_DOCUMENTS);
    } else {
      if (current.size >= MAX_CONTEXT_SOURCE_DOCUMENTS) {
        this.contextDocSelectionLimitReached.set(true);
        return;
      }
      current.add(docId);
      this.contextDocSelectionLimitReached.set(false);
    }
    this.selectedContextDocIds.set(current);
  }

  /** Select / clear every currently visible (search-filtered) doc. */
  toggleAllContextDocs(): void {
    const visibleIds = this.contextDocRows().map(r => r.context_document_id);
    this.selectedContextDocIds.update(ids => {
      const allSelected = visibleIds.length > 0 && visibleIds.every(id => ids.has(id));
      const next = new Set(ids);
      if (allSelected) {
        for (const id of visibleIds) next.delete(id);
        this.contextDocSelectionLimitReached.set(next.size > MAX_CONTEXT_SOURCE_DOCUMENTS);
      } else {
        for (const id of visibleIds) {
          if (next.has(id)) continue;
          if (next.size >= MAX_CONTEXT_SOURCE_DOCUMENTS) {
            this.contextDocSelectionLimitReached.set(true);
            break;
          }
          next.add(id);
        }
        if (next.size <= MAX_CONTEXT_SOURCE_DOCUMENTS) {
          this.contextDocSelectionLimitReached.set(false);
        }
      }
      return next;
    });
  }

  onUploadModalClosed(docs: ContextDocument[]): void {
    this.showUploadModal.set(false);
    if (docs.length > 0) {
      this.contextDocSelectionLimitReached.set(false);
      // Add newly uploaded docs to the list and auto-select them
      const current = this.contextDocuments();
      const existingIds = new Set(current.map(d => d.context_document_id));
      const newDocs = docs.filter(d => !existingIds.has(d.context_document_id));
      this.contextDocuments.set([...newDocs, ...current]);

      const selected = new Set(this.selectedContextDocIds());
      for (const doc of newDocs) {
        if (selected.has(doc.context_document_id)) continue;
        if (selected.size >= MAX_CONTEXT_SOURCE_DOCUMENTS) {
          this.contextDocSelectionLimitReached.set(true);
          break;
        }
        selected.add(doc.context_document_id);
      }
      this.selectedContextDocIds.set(selected);

      // A brand-new upload is still being extracted — watch it through to
      // a terminal status so Create unblocks on its own.
      this.pollSelectedDocExtraction();
    }
  }

  onUploadModalCancelled(): void {
    this.showUploadModal.set(false);
  }

  // ── Submit ─────────────────────────────────────────────────────────

  private submitAssessment(): void {
    const name = this.assessmentName().trim();
    if (
      !name ||
      this.isTerminalState() ||
      this.hasExtractingSelection() ||
      (this.showPreQuestionnaireStep() &&
        (!this.canConfirmPreQuestionnaire() || !this._descriptionReviewed()))
    ) {
      return;
    }
    const customerOrgName = this.isForMyself() ? null : this.customerOrgName().trim() || null;
    const labelDict: Record<string, string> = {};
    this.selectedLabels().forEach(l => {
      labelDict[l.label_id] = l.label_name;
    });
    const contextDocIds = [...this.selectedContextDocIds()];
    if (contextDocIds.length > MAX_CONTEXT_SOURCE_DOCUMENTS) {
      this.contextDocSelectionLimitReached.set(true);
      this.setStep('context-docs');
      return;
    }
    // Resolve the emitted type in order of specificity:
    //   1. User picked one on the type step → emit that.
    //   2. Single-registered-type build (type step never shown) →
    //      emit the sole registered type.
    //   3. Defensive fallback for a mis-configured build → AI Risk.
    const emittedType =
      this._selectedType() ??
      this.registeredTypes[0]?.type ??
      AssessmentTypeEnum.AI_RISK_ASSESSMENT;
    // FE-G: emit the activity id only when the user is in linked mode
    // AND the type actually supports PA-scoping. Standalone (or non-DPIA
    // types) send null so the handler dispatches to `createAssessment`.
    const activityId =
      this.showLinkStep() && this._isLinkedToPa() ? (this._selectedActivityId() ?? null) : null;
    // VER-331: vendor-shaped path — vendor id + the gate-preview's
    // resolved country carry through to the vendor-start endpoint.
    const vendorId = this.showVendorStep() ? this.selectedVendorId() : null;
    const countryCode = this.showVendorStep() ? (this._gatePreview()?.country_code ?? null) : null;
    // VER-377: a ticked derogation is either only recorded (no TIA) or
    // run as a voluntary full TIA; "Assess anyway" is always a full TIA.
    const art49Derogation: Art49DerogationClaim | null =
      this.showDerogationsStep() && this.hasDerogationClaim()
        ? {
            derogation_ids: this.selectedDerogationList(),
            rationale: this._derogationRationale().trim(),
          }
        : null;
    const fullAssessment =
      this._assessAnyway() ||
      !!this._supersedesLinkId() ||
      (art49Derogation !== null && this._derogationOutcome() === 'full');
    this.confirmed.emit({
      questionnaireVersion: this.showPreQuestionnaireStep()
        ? (this._questionnaireVersion() ?? undefined)
        : undefined,
      preQuestionnaireAnswers: this.showPreQuestionnaireStep()
        ? { ...this._preQuestionnaireAnswers() }
        : undefined,
      assessmentName: name,
      selectedLabels: labelDict,
      customer_org_name: customerOrgName,
      context_document_ids: contextDocIds.length > 0 ? contextDocIds : undefined,
      ...(contextDocIds.length
        ? { context_source_owner: { kind: 'session' as const, id: this.draftSourceOwnerId() } }
        : {}),
      language: this.assessmentLanguage(),
      assessmentType: emittedType,
      activityId,
      vendorId,
      countryCode,
      art49Derogation,
      fullAssessment: this.showVendorStep() ? fullAssessment : undefined,
      recordSubject: this.showVendorStep() ? this.transferSubject() : null,
      supersedesLinkId: this.showVendorStep() ? this._supersedesLinkId() : null,
    });
    // Do NOT reset state here — the modal is still visible.
    // State resets when the modal visibility changes to false (see constructor effect).
  }

  private resetState(): void {
    this.activityCheckSub?.unsubscribe();
    this.activityCheck.set('idle');
    this.activitiesSub?.unsubscribe();
    this.existingDpias.set(new Map());
    this.activityLoadFailed.set(false);
    // Cancel pending async work first to prevent orphaned API calls
    if (this.searchDebounceTimer) {
      clearTimeout(this.searchDebounceTimer);
      this.searchDebounceTimer = null;
    }
    if (this.checkDebounceTimer) {
      clearTimeout(this.checkDebounceTimer);
      this.checkDebounceTimer = null;
    }
    this.extractionPollSub?.unsubscribe();
    this.extractionPollSub = null;
    this.removeScrollListener();

    // Reset all form / wizard state. Initial step depends on whether
    // the org has multiple assessment types entitled — multi-type
    // orgs start at 'type' (picker); single-type orgs skip to 'details'.
    // Evaluated at reset (not at signal-init) so a feature-flag toggle
    // between opens takes effect.
    this._selectedType.set(null);
    const initialStep = this.registeredTypes.length > 1 ? 'type' : 'language';
    this._currentStep.set(initialStep);
    this._maxReachedStep.set(initialStep);
    this._isLinkedToPa.set(true);
    this._selectedActivityId.set(null);
    this._availableActivities.set([]);
    this._isLoadingActivities.set(false);
    this._vendorList.set([]);
    this._isLoadingVendors.set(false);
    this._selectedVendorId.set(null);
    this._gatePreview.set(null);
    this._isLoadingGatePreview.set(false);
    this._gatePreviewError.set(null);
    this._assessAnyway.set(false);
    this.resetDerogations();
    this._supersedesLinkId.set(null);
    this.assessmentName.set('');
    this.selectedLabels.set([]);
    this.clearLabelInput();
    this.labelAutocompleteOpen.set(false);
    this.currentPage.set(1);
    this.availableLabels.set([]);
    this.totalLabels.set(0);
    this.isLoadingLabels.set(false);
    this.isCheckingLabel.set(false);
    this.labelExists.set(false);
    this.hasCheckedLabel.set(false);
    this.labelCreationError.set(null);
    this.isForMyself.set(true);
    this.customerOrgName.set('');
    this.assessmentLanguage.set(this.stateService.orgDefaultContentLanguage());
    this._languageManuallySet.set(false);
    this.contextDocuments.set([]);
    this.contextDocsLoadSub?.unsubscribe();
    this.contextDocsLoadSub = null;
    this.contextDocsLoadRequest++;
    this.contextDocsLoadError.set(null);
    this.selectedContextDocIds.set(new Set());
    this.contextDocSelectionLimitReached.set(false);
    this.contextDocSearch.set('');
    this.isLoadingContextDocs.set(false);
    this.showUploadModal.set(false);
    this._preQuestionnaireAnswers.set({});
    this._descriptionEdits.set({});
    this._dismissedDescriptions.set(new Set());
    this._questionnaireVersion.set(null);
    this._descriptionReviewed.set(false);
    this.descriptionSuggestion.reset();
  }
}
